import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { InformBox } from "@/components/inform-box/InformBox.tsx";
import i18n from "@/i18n.ts";
import { SurveyEditSidebar } from "@/pages/talent/surveys/edit/components/SurveyEditSidebar.tsx";
import {
  SurveyEditPageProps,
  useSurveyEditPage,
} from "@/pages/talent/surveys/edit/pages/survey-edit-pages.tsx";
import { SurveyEditTitleSlot } from "@/pages/talent/surveys/edit/pages/SurveyEditTitleSlot.tsx";
import { SurveyEditPageStep } from "@/pages/talent/surveys/edit/survey-edit.ts";
import { useSurveyDetails, useSurveyUpdateLaunch } from "@/queries/surveys/use-surveys.query.ts";
import { useSurveyFormById } from "@/queries/surveys/use-survey-forms.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ApiSurveyUpdateLaunch } from "@/types/survey.types.ts";
import { ApiFormElementType } from "@/types/forms/form.types.ts";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Button, FullScreenPage, Input, Stack, Switch, Title } from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";

const buildSurveyEditLaunchSchema = () =>
  yup.object({
    date_starts_at: yup.date().defined().nullable(),
    date_ends_at: yup
      .date()
      .defined()
      .nullable()
      .when("date_starts_at", {
        is: (date_starts_at: Date | null) => date_starts_at !== null,
        then: (schema) =>
          schema.min(
            yup.ref("date_starts_at"),
            i18n.t("errors:end_date_should_be_greater_start_date")
          ),
      }),
    anonymous_threshold: yup
      .number()
      .required(i18n.t("errors:field_required"))
      .when("$isAnonymous", {
        is: true,
        then: (schema) => schema.min(3, i18n.t("surveys:anonymous_threshold_min_value")),
      }),
  });

export const SurveyEditLaunchPage = ({ survey }: SurveyEditPageProps) => {
  const { t } = useTranslation();

  const { confirmExit, navigateToStep } = useSurveyEditPage();
  const [isAnonymous, setIsAnonymous] = useState<boolean>(survey.anonymous_threshold > 0);
  const [isTimeFrame, setIsTimeFrame] = useState<boolean>(survey.date_ends_at !== null);

  const { data: surveyData } = useSurveyDetails(survey.id);
  const { data: surveyForm } = useSurveyFormById(
    survey.id,
    Boolean(survey.form_id || survey.form_template_id)
  );
  const hasFileQuestion =
    surveyForm?.pages.some((page) =>
      page.sections.some((section) =>
        section.elements.some((element) => element.type === ApiFormElementType.file)
      )
    ) ?? false;

  const canEditAnonymous =
    (surveyData && surveyData.participants_stats.completed_count == 0) || false;

  const surveyEditLaunchSchema = buildSurveyEditLaunchSchema();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ApiSurveyUpdateLaunch>({
    mode: "onSubmit",
    resolver: yupResolver(surveyEditLaunchSchema),
    values: {
      date_starts_at: survey.date_starts_at,
      date_ends_at: survey.date_ends_at,
      anonymous_threshold: survey.anonymous_threshold,
    },
    context: {
      isAnonymous,
    },
  });
  const surveyUpdate = useSurveyUpdateLaunch();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await surveyUpdate.mutateAsync({
        id: survey.id,
        payload: data,
      });
      analyticsService.trackEvent(analyticEvents.surveys.edited, {
        [analyticProperties.id]: survey.id,
        [analyticProperties.tab]: SurveyEditPageStep.launch,
      });
      navigateToStep(survey.id, SurveyEditPageStep.notifications);
    } catch {
      // Handle error
    }
  });

  return (
    <FullScreenPage
      backSlot={
        <Button variant={"secondary"} size={"lg"} onClick={() => confirmExit(survey.id)}>
          {t("common:exit")}
        </Button>
      }
      titleSlot={<SurveyEditTitleSlot step={SurveyEditPageStep.launch} survey={survey} />}
      isCenterVertical={false}
      actionsSlot={
        <>
          <Button
            variant={"secondary"}
            size={"lg"}
            onClick={() => {
              navigateToStep(survey.id, SurveyEditPageStep.questions);
            }}
          >
            {t("common:back")}
          </Button>
          <Button size={"lg"} onClick={onSubmit}>
            {t("common:continue")}
          </Button>
        </>
      }
      sidebarSlot={<SurveyEditSidebar step={SurveyEditPageStep.launch} survey={survey} />}
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"sm"} justify={"center"}>
          <Stack className={"card-panel"} gap={"lg"}>
            <Title header={t("common:launch_plan")} size={"lg"} paddingBottom={true} />
            <Stack gap={"sm"}>
              <Switch
                checked={isTimeFrame}
                onChange={(value) => {
                  setIsTimeFrame(value);
                  if (value) {
                    // setValue("anonymous_threshold", 3);
                  } else {
                    setValue("date_starts_at", null);
                    setValue("date_ends_at", null);
                  }
                }}
                title={t("common:set_timeframe")}
              />
              {isTimeFrame && (
                <Box className={"grid grid-cols-2 gap-(--gap-sm)"}>
                  <Controller
                    control={control}
                    name="date_starts_at"
                    render={({ field: { value, onChange } }) => (
                      <Datepicker
                        value={value}
                        onChange={onChange}
                        label={t("common:date_from")}
                        isClearable={false}
                        error={errors.date_starts_at?.message}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="date_ends_at"
                    render={({ field: { value, onChange } }) => (
                      <Datepicker
                        value={value}
                        onChange={onChange}
                        label={t("common:date_to")}
                        isClearable={false}
                        error={errors.date_ends_at?.message}
                      />
                    )}
                  />
                </Box>
              )}
            </Stack>

            <Stack gap={"sm"}>
              <Switch
                checked={isAnonymous}
                disabled={!canEditAnonymous}
                onChange={(value) => {
                  setIsAnonymous(value);
                  if (value) {
                    setValue("anonymous_threshold", 3);
                  } else {
                    setValue("anonymous_threshold", 0);
                  }
                }}
                title={t("surveys:make_anonymous")}
              />
              {isAnonymous && (
                <>
                  <Input
                    label={t("surveys:anonymous_threshold")}
                    disabled={!canEditAnonymous}
                    type={"number"}
                    {...register("anonymous_threshold")}
                  />
                  {hasFileQuestion && (
                    <InformBox
                      emoji=":warning:"
                      title={t("surveys:anonymous_file_warning_title")}
                      description={t("surveys:anonymous_file_warning_description")}
                    />
                  )}
                </>
              )}
            </Stack>
          </Stack>
          <Button type={"submit"} size={"lg"} isLoading={surveyUpdate.isPending}>
            {t("common:continue")}
          </Button>
        </Stack>
      </form>
    </FullScreenPage>
  );
};
